<template>
  <div class="corps-summary">
    <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
      <h3 class="font-md">Остатки корпоративных клиентов</h3>
      <v-spacer />
      <v-select
        v-model="tests"
        :items="testOptions"
        label="Тесты"
        placeholder="Все тесты"
        multiple
        small-chips
        deletable-chips
        clearable
        outlined dense hide-details
        style="max-width: 320px"
      />
      <v-select
        v-model="activityStatus"
        :items="activityStatuses"
        label="Статус кабинета"
        placeholder="Любой"
        clearable outlined dense hide-details
        style="max-width: 200px"
      />
      <v-select v-model="period" :items="periods" label="Период движений" outlined dense hide-details style="max-width: 220px" />
      <v-btn small outlined :disabled="!rows.length" @click="exportXlsx">
        <v-icon left small>mdi-file-excel-outline</v-icon> Выгрузить
      </v-btn>
    </div>
    <div class="caption grey--text text--darken-1 mb-3">{{ periodHint }}</div>

    <StateBlock v-if="loading && !rows.length" state="loading" compact />
    <StateBlock v-else-if="error" state="error" compact @retry="load" />
    <v-data-table
      v-else
      :headers="headers"
      :items="rows"
      :items-per-page="10"
      item-key="id"
      show-expand
      single-expand
      :footer-props="{ 'items-per-page-options': [10, 25, 50, -1], 'items-per-page-text': 'На странице:' }"
      sort-by="spent"
      sort-desc
      no-data-text="Корпоративных клиентов нет"
    >
      <template v-slot:item.company="{ item }">
        <router-link class="text-primary" :to="{ name: 'clients-id', params: { id: item.id, tab: 'analytics' } }">{{ item.company }}</router-link>
      </template>
      <template v-slot:item.balance="{ item }">
        <span v-if="item.balance === null">—</span>
        <span v-else :class="{ 'error--text': item.balance < 0 }">{{ item.balance }}</span>
      </template>
      <template v-slot:item.lastTopUp="{ item }">{{ formatDate(item.lastTopUp) }}</template>
      <template v-slot:item.activity="{ item }">
        <CorpActivityChip :source="item.activitySource" short dash />
      </template>
      <template v-slot:item.received="{ item }">{{ item.received === null ? '—' : item.received }}</template>
      <template v-slot:item.spent="{ item }">{{ item.spent === null ? '—' : item.spent }}</template>
      <template v-slot:expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="py-3">
          <div v-if="!item.items.length" class="caption grey--text">По выбранным тестам движений и остатков нет</div>
          <v-simple-table v-else dense style="max-width: 640px">
            <thead>
              <tr><th>Тест</th><th class="text-right">Остаток</th><th class="text-right">Получено</th><th class="text-right">Израсходовано</th></tr>
            </thead>
            <tbody>
              <tr v-for="test in item.items" :key="test.id">
                <td>{{ test.name }}</td>
                <td class="text-right" :class="{ 'error--text': test.total.balance < 0 }">{{ test.total.balance }}</td>
                <td class="text-right">{{ test.total.received }}</td>
                <td class="text-right">{{ test.total.spent }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from 'moment';
import * as XLSX from 'xlsx';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import CorpActivityChip from '@/components/clients/card/CorpActivityChip.vue';
import { ACTIVITY_STATUS_OPTIONS, parseCorpActivity } from '@/common/models/corpActivity';
import { hasAmounts, parseWalletStatistics } from '@/common/models/walletStatistics';
import { PERIOD_HINT, WALLET_PERIODS, walletPeriodRange } from '@/components/clients/card/walletPeriods';

// Сводная аналитика админа по корпам (Э-7): компания, остаток, получено и израсходовано за период, последнее пополнение,
// партнёр. Тесты выбираются множественно: суммы пересчитываются по выбранным, строка раскрывается по тестам.
export default {
  name: 'CorpsWalletSummary',
  components: { StateBlock, CorpActivityChip },
  data: () => ({
    activityStatus: null,
    activityStatuses: ACTIVITY_STATUS_OPTIONS,
    period: '7d',
    tests: [],
    periods: WALLET_PERIODS.filter((p) => p.value !== 'all'),
    periodHint: PERIOD_HINT,
    clients: [],
    partners: [],
    loading: false,
    error: false,
    requestId: 0,
  }),
  computed: {
    headers() {
      return [
        { text: 'Компания', value: 'company' },
        { text: 'Общий остаток', value: 'balance', align: 'end' },
        { text: 'Получено за период', value: 'received', align: 'end' },
        { text: 'Израсходовано за период', value: 'spent', align: 'end' },
        { text: 'Последнее пополнение', value: 'lastTopUp' },
        { text: 'Кабинет', value: 'activity', sortable: false },
        { text: 'Партнёр', value: 'partner' },
        { text: '', value: 'data-table-expand', sortable: false },
      ];
    },
    wallets() {
      return this.clients.map((c) => ({ client: c, wallet: parseWalletStatistics(c.wallet_statistics) }));
    },
    // Все тесты, встречающиеся в кошельках корпов
    testOptions() {
      const byId = new Map();
      this.wallets.forEach(({ wallet }) => {
        if (wallet) wallet.items.forEach((i) => byId.set(i.id, i.name));
      });
      return [...byId].map(([value, text]) => ({ value, text })).sort((a, b) => a.text.localeCompare(b.text, 'ru'));
    },
    rows() {
      const selected = new Set(this.tests);
      return this.wallets.map(({ client: c, wallet }) => {
        const items = wallet
          ? wallet.items.filter((i) => (!selected.size || selected.has(i.id)) && hasAmounts(i.total))
          : [];
        // Без выбранных тестов берём итог сервера, с выбранными — сумму по ним
        const amount = (key) => {
          if (!wallet) return null;
          return selected.size ? items.reduce((acc, i) => acc + i.total[key], 0) : wallet.totals[key];
        };
        const lastTopUp = moment(c.last_buffer_replenishment_date || null);
        // Партнёр приходит id или объектом
        const s = c.supervisor && typeof c.supervisor !== 'object'
          ? this.partners.find((p) => String(p.id) === String(c.supervisor))
          : c.supervisor;
        return {
          id: c.id,
          company: c.company || '—',
          items,
          balance: amount('balance'),
          received: amount('received'),
          spent: amount('spent'),
          lastTopUp: lastTopUp.isValid() ? lastTopUp.format('YYYY-MM-DD') : '',
          partner: s && typeof s === 'object' ? [s.last_name, s.first_name].filter(Boolean).join(' ') : '—',
          activitySource: c,
        };
      });
    },
  },
  watch: {
    period() {
      this.load();
    },
    activityStatus() {
      this.load();
    },
  },
  methods: {
    formatDate(iso) {
      return iso ? moment(iso).format('DD.MM.YYYY') : '—';
    },
    async load() {
      this.requestId += 1;
      const { requestId } = this;
      const { from, to } = walletPeriodRange(this.period);
      this.loading = true;
      this.error = false;
      try {
        const params = { page_size: -1, from_date: from, to_date: to };
        // Срок кабинета фильтрует сервер
        if (this.activityStatus) params.activity_status = this.activityStatus;
        const response = await api.get('/users/corp_clients/', {
          params,
          timeout: 120000,
        });
        if (requestId !== this.requestId) return;
        this.clients = Array.isArray(response) ? response : (response.results || []);
      } catch (e) {
        if (requestId !== this.requestId) return;
        this.error = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async loadPartners() {
      try {
        const response = await api.get('/users/partners/', { params: { page_size: -1 } });
        this.partners = Array.isArray(response) ? response : (response.results || []);
      } catch (e) {
        this.partners = [];
      }
    },
    // Без персональных данных: только компания и цифры (ФИО партнёра не выгружаем).
    exportXlsx() {
      const period = this.periods.find((p) => p.value === this.period);
      const data = [
        [`Период движений: ${period ? period.text : ''}`],
        [],
        [`Тесты: ${this.tests.length ? this.testOptions.filter((t) => this.tests.includes(t.value)).map((t) => t.text).join(', ') : 'все'}`],
        ['Компания', 'Общий остаток', 'Получено за период', 'Израсходовано за период', 'Последнее пополнение', 'Кабинет'],
        ...this.rows.map((r) => [r.company, r.balance, r.received, r.spent, this.formatDate(r.lastTopUp), (parseCorpActivity(r.activitySource) || {}).label || '']),
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = [{ wch: 40 }, { wch: 16 }, { wch: 20 }, { wch: 24 }, { wch: 22 }, { wch: 26 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Корп. клиенты');
      XLSX.writeFile(wb, 'Остатки корпоративных клиентов.xlsx');
    },
  },
  created() {
    this.load();
    this.loadPartners();
  },
};
</script>

<style scoped lang="scss">
.corps-summary {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}
</style>
